import { useState } from "react";

import Box from "@/components/Box";
import PenIcon from "@/images/PenIcon";
import Group from "@/tpo/Group";
import Modal from "@/tpo/Modal";
import Stack from "@/tpo/Stack";
import ButtonV2 from "@/v2/Buttons";
import { useMutation } from "@apollo/client";

import { REQUEST_QUESTIONNAIRE_MUTATION } from "./graphql/queries";

function getModalMessage({ isActive, hasGuardian }) {
  if (hasGuardian && isActive) {
    return `This patient has a guardian with access to the Regenerus platform. We'll email the guardian on your behalf with a request to complete the questionnaire, or they can log in and access it directly. Once submitted, their responses will appear in this view.`;
  }
  if (hasGuardian && !isActive) {
    return `This patient has a guardian who has not yet signed up to the Regenerus patient platform. We will send an email to their guardian on your behalf inviting them to create an account and complete the questionnaire on the patient's behalf. Once registered, the guardian can log in to access it directly at any time.
Their responses will appear in this view once the questionnaire has been submitted.`;
  }
  if (isActive) {
    return `This patient already has access to the Regenerus patient platform. We will send an email on your behalf inviting them to complete the questionnaire. Alternatively, they can log in to their platform and access it directly.
Once submitted, their responses will appear in this view.`;
  }
  return `This patient has not yet signed up to the Regenerus patient platform. We will send an email on your behalf inviting them to create an account and complete the questionnaire. Once registered, they can log in to access it directly at any time.
Their responses will appear in this view once the questionnaire has been submitted.`;
}

export default function RequestQuestionnaireModal({
  show,
  onClose,
  patientId,
  organisationId,
  isActive,
  hasGuardian
}) {
  const [submitted, setSubmitted] = useState(false);
  const [requestQuestionnaire, { loading }] = useMutation(REQUEST_QUESTIONNAIRE_MUTATION);

  const handleConfirm = async () => {
    const { data } = await requestQuestionnaire({
      variables: {
        input: {
          userId: patientId,
          organisationId
        }
      }
    });

    const errors = data?.requestQuestionnaireMutation?.errors;
    if (!errors || errors.length === 0) {
      setSubmitted(true);
    }
  };

  const handleClose = () => {
    setSubmitted(false);
    onClose();
  };

  return (
    <Modal
      show={show}
      close={handleClose}
      mode={["fullScreen", "fullScreen", "centered"]}
      maxWidth={1024}
      bg="white"
      closeButton
      headerProps={{ p: [20, 20, 40] }}
    >
      <Stack
        gap={40}
        px={[20, 20, 40]}
        pb={[20, 20, 80]}
        alignItems="center"
        mx="auto"
        maxWidth={720}
      >
        <Group gap={20} alignItems="center">
          <PenIcon size={[20, 20, 50]} variant="fill" />
          <Box fontFamily="gilroyBold" fontSize={[20, 20, 44]}>
            Questionnaire Request
          </Box>
        </Group>
        {submitted ? (
          <Stack gap={24}>
            <Box
              fontFamily="gilroyMedium"
              fontSize={[16, 16, 18]}
              lineHeight="28px"
              textAlign="center"
            >
              Your request has been sent
              {hasGuardian ? " to the patient's guardian" : " to your patient"} and their response
              will be available to view once their questionnaire has been submitted.
            </Box>
          </Stack>
        ) : (
          <Stack gap={40}>
            <Box fontFamily="gilroyMedium" fontSize={[16, 16, 18]} lineHeight="28px" textAlign="center">
              {getModalMessage({ isActive, hasGuardian })}
            </Box>
            <Box display="flex" justifyContent="center">
              <ButtonV2
                color="green"
                onClick={handleConfirm}
                disabled={loading}
                size={["sm", "sm", "md"]}
              >
                confirm and send
              </ButtonV2>
            </Box>
          </Stack>
        )}
      </Stack>
    </Modal>
  );
}
